import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ChevronsLeftRight } from "lucide-react";

/**
 * Draggable before/after slider with auto-demo animation on scroll into view.
 * Props: before, after, beforeLabel, afterLabel, aspect (Tailwind class)
 */
export const BeforeAfter = ({
  before,
  after,
  beforeLabel = "Before",
  afterLabel = "After",
  aspect = "aspect-[16/10]",
  testId,
}) => {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const draggingRef = useRef(false);
  const inView = useInView(containerRef, { once: true, margin: "-80px" });
  const hasUserInteractedRef = useRef(false);
  const demoRafRef = useRef(null);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const x = e.touches ? e.touches[0].clientX : e.clientX;
      updateFromClientX(x);
    };
    const onUp = () => (draggingRef.current = false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [updateFromClientX]);

  // Auto-demo: animate through a smooth sequence when the slider enters view.
  useEffect(() => {
    if (!inView) return;
    // key-frames (time%, position%)
    const keys = [
      [0, 50],
      [22, 18],
      [50, 82],
      [78, 40],
      [100, 50],
    ];
    const dur = 3200;
    const start = performance.now() + 350; // small delay
    const interp = (t) => {
      for (let i = 1; i < keys.length; i++) {
        if (t <= keys[i][0]) {
          const [t0, p0] = keys[i - 1];
          const [t1, p1] = keys[i];
          const r = (t - t0) / (t1 - t0);
          const eased = r < 0.5 ? 2 * r * r : 1 - Math.pow(-2 * r + 2, 2) / 2;
          return p0 + (p1 - p0) * eased;
        }
      }
      return 50;
    };
    const tick = (now) => {
      if (hasUserInteractedRef.current) return;
      const elapsed = now - start;
      if (elapsed < 0) {
        demoRafRef.current = requestAnimationFrame(tick);
        return;
      }
      const t = Math.min((elapsed / dur) * 100, 100);
      setPos(interp(t));
      if (elapsed < dur) demoRafRef.current = requestAnimationFrame(tick);
    };
    demoRafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(demoRafRef.current);
  }, [inView]);

  const startDrag = (e) => {
    hasUserInteractedRef.current = true;
    if (demoRafRef.current) cancelAnimationFrame(demoRafRef.current);
    draggingRef.current = true;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    updateFromClientX(x);
  };

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      className={`relative w-full ${aspect} overflow-hidden rounded-2xl border border-[#E5DEE5] bg-black select-none cursor-ew-resize`}
      onMouseDown={startDrag}
      onTouchStart={startDrag}
    >
      {/* AFTER image (full) */}
      <img
        src={after}
        alt={afterLabel}
        className="absolute inset-0 w-full h-full object-cover"
        draggable="false"
      />
      {/* BEFORE image clipped via clip-path (no layout dependency) */}
      <img
        src={before}
        alt={beforeLabel}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        draggable="false"
      />

      {/* Labels */}
      <span className="absolute top-4 left-4 z-10 rounded-full bg-black/60 backdrop-blur px-3 py-1.5 text-white text-[10px] md:text-xs tracking-[0.25em] uppercase font-semibold">
        {beforeLabel}
      </span>
      <span className="absolute top-4 right-4 z-10 rounded-full bg-black/60 backdrop-blur px-3 py-1.5 text-white text-[10px] md:text-xs tracking-[0.25em] uppercase font-semibold">
        {afterLabel}
      </span>

      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 w-[2px] bg-white pointer-events-none shadow-[0_0_18px_rgba(255,255,255,0.35)]"
        style={{ left: `calc(${pos}% - 1px)` }}
      />

      {/* Drag handle with pulse hint until user interacts */}
      <motion.button
        aria-label="Drag to compare"
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        animate={
          !hasUserInteractedRef.current
            ? { boxShadow: [
                "0 0 0 0 rgba(200,90,84,0.6)",
                "0 0 0 14px rgba(200,90,84,0.0)",
                "0 0 0 0 rgba(200,90,84,0.6)",
              ] }
            : {}
        }
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 rounded-full brand-gradient shadow-xl flex items-center justify-center text-white cursor-ew-resize z-20 hover:scale-110 transition-transform duration-200"
        style={{ left: `${pos}%` }}
      >
        <ChevronsLeftRight size={20} />
      </motion.button>

      {/* Hint text (only visible before interaction) */}
      {!hasUserInteractedRef.current && (
        <span className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 rounded-full bg-black/50 backdrop-blur px-3 py-1 text-white/90 text-[10px] tracking-[0.25em] uppercase font-semibold">
          Drag to compare
        </span>
      )}
    </div>
  );
};
